/* eslint-disable react/forbid-dom-props */ function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import * as PropTypes from 'prop-types';
import { Stack, Text, Image } from '@fluentui/react';
import { v4 } from 'uuid';
import SpzaComponent from '@shared/components/spzaComponent';
import { PricingStates, AppTag } from '@shared/Models';
import { DataMap } from '@shared/utils/dataMapping';
import { BaseTile } from '@shared/containers/baseTile';
import { Constants, OfferType } from '@shared/utils/constants';
import { isOfficeNonSaasApp, isOfficeApp, isSaaS, getParentProductTitle, getCTATypeForActionString, isTransactApp, isAvailableInThisRegion, isPrivateOffer, getPDPRoute, isBagTransactable, isPowerBIVisuals, getCtaBagTransactable, getBadgeTypes, getProductByUrlKey } from '@shared/utils/appUtils';
import { generateTileClickPayloadAndLogTelemetry, getProductsList, hideCertificateBadges } from '@shared/utils/detailUtils';
import { getStartingFromPriceText } from '@shared/utils/pricing';
import { getComponentXYCoordinate, getTileComponentRank } from '@shared/utils/reactUtils';
import { isAppSource, isEmbed } from '@embed/embedHostUtils';
import { getPrimaryProductUrl, bitMasksMatchFilter } from '@shared/utils/datamappingHelpers';
import { SharedColors } from '@fluentui/theme';
import PBICertified from '@shared/images/badges/PBICertified.svg';
import AzureExpertsMSP from '@shared/images/badges/AzureExpertsMSP.svg';
import PrivateOffer from '@shared/images/badges/PrivateOffer.svg';
import OfficeAwards from '@shared/images/badges/OfficeAwards.svg';
import SolutionMap from '@shared/images/badges/SolutionMap.svg';
import MicrosoftCertified from '@shared/images/badges/MicrosoftCertified.svg';
import AzureBenefitEligibleIMG from '@shared/images/badges/AzureBenefitEligible.svg';
import CertifiedSoftware from '@shared/images/badges/badge_CertifiedSoftware.svg';
import freeTrial from '@shared/images/tileFreeTrialBadge.svg';
import { ItemsTooltip } from '@shared/components/ItemsTooltip';
import { isOneTimePaymentOffer } from '@shared/utils/onetimepaymentoffers';
import { getRatingSummaryByType } from '@shared/utils/reviewsUtils';
const { CTAType: { Install: CTAInstall, BuyLicense: CTABuyLicense, Get: CTAGet, RequestTrial: CTARequestTrial, Try: CTATry, Purchase: CTAPurchase }, ActionStrings: { Install: ActionInstall, BuyLicense: ActionBuyLicense, Get: ActionGet, RequestTrial: ActionRequestTrial, Try: ActionTry }, JsllCTAId: { Install: JsllCTAIdInstall, Try: JsllCTAIdTry, BuyLicense: JsllCTAIdBuyLicense } } = Constants;
const badgesInformation = [
    {
        badge: Constants.BadgeType.AzureBenefitEligible,
        loc: 'Global_Loc_Azure_benefit',
        title: 'Azure benefit eligible',
        src: AzureBenefitEligibleIMG
    },
    {
        badge: Constants.BadgeType.MicrosoftCertified,
        loc: 'Microsoft365Certified_Badge',
        title: 'Microsoft 365 Certified',
        src: MicrosoftCertified,
        hidden: ()=>hideCertificateBadges()
    },
    {
        badge: Constants.BadgeType.PBICertified,
        loc: 'PowerBICertified_Badge',
        title: 'Power BI Certified',
        src: PBICertified
    },
    {
        badge: Constants.BadgeType.CertifiedSoftware,
        loc: 'CertifiedSoftware',
        title: 'Certified software',
        src: CertifiedSoftware
    },
    {
        badge: Constants.BadgeType.PreferredSolution,
        loc: 'MicrosoftPreferredSolution_Badge',
        title: 'Microsoft preferred solution',
        src: SolutionMap
    },
    {
        badge: Constants.BadgeType.AzureExpertsMSP,
        loc: 'AzureExpertsMSP_Badge',
        title: 'Azure Experts MSP',
        src: AzureExpertsMSP
    },
    {
        badge: Constants.BadgeType.Microsoft365AppAward,
        loc: 'Microsoft365AppAward_Badge',
        title: 'Microsoft 365 App Award',
        src: OfficeAwards
    },
    {
        badge: Constants.BadgeType.PrivateOffer,
        loc: 'UserPrivatePlan_Badge',
        title: 'Private Plan',
        src: PrivateOffer,
        customClassName: 'size-14'
    },
    {
        badge: Constants.BadgeType.FreeTrial,
        loc: 'FreeTrial_Badge',
        title: 'Free trial',
        src: freeTrial
    }
];
export class AppTile extends SpzaComponent {
    handleKeyPress(event) {
        // only enter key should open the corresponding pop up
        if (event.charCode === Constants.SystemKey.Enter) {
            if (event.preventDefault) {
                event.preventDefault();
            }
            this.getApp(event);
        }
    }
    getApp(e) {
        e.stopPropagation();
        const { closeTile, currentView, actionString, ctaTypes, licenseManagement, entityId, AverageRating, NumberOfRatings, doesTenantHasLicenseForApp, linkedAddIns, uiRole, activeFilters, searchSortingOption, count, searchReqId, setTileCheckoutSource, ribbonKey } = this.props;
        if (closeTile && currentView === Constants.currentView.AppDetails) {
            this.props.closeTile();
        }
        let ctaType = getCTATypeForActionString(actionString);
        // Since Get and Purchase have the same action strings, fix the CTA type for purchase
        if (ctaTypes.length && ctaTypes[0] === CTAPurchase) {
            ctaType = CTAPurchase;
        }
        if (this.isPBIVApp) {
            ctaType = CTAGet;
        } else if (isBagTransactable(this.props)) {
            ctaType = getCtaBagTransactable(uiRole, doesTenantHasLicenseForApp, licenseManagement === null || licenseManagement === void 0 ? void 0 : licenseManagement.isFreemium);
        } else if (licenseManagement === null || licenseManagement === void 0 ? void 0 : licenseManagement.isMicrosoftManaged) {
            var _this_getCtaMicrosoftManaged;
            ctaType = (_this_getCtaMicrosoftManaged = this.getCtaMicrosoftManaged()) === null || _this_getCtaMicrosoftManaged === void 0 ? void 0 : _this_getCtaMicrosoftManaged.type;
        }
        const reference = this.refs[`appTile ${entityId}`];
        const coordinate = getComponentXYCoordinate(reference);
        const tileRank = getTileComponentRank(reference);
        const badgeTypes = getBadgeTypes(this.props);
        setTileCheckoutSource();
        const details = {
            id: entityId,
            rank: JSON.stringify(tileRank),
            xOffset: coordinate.x,
            yOffset: coordinate.y,
            CTAType: ctaType,
            currentView,
            appAverageRating: AverageRating,
            appNumOfRatings: NumberOfRatings,
            isMicrosoftManaged: licenseManagement && licenseManagement.isMicrosoftManaged,
            isFreemium: licenseManagement && licenseManagement.isFreemium,
            hasLicense: doesTenantHasLicenseForApp,
            isSaaSBundle: false,
            discoveryData: {
                totalNumOfResults: count,
                activeFilters,
                sortingBy: searchSortingOption,
                searchRequestId: searchReqId
            },
            badge: JSON.stringify(badgeTypes)
        };
        // Add Legacy Telemetry Properties for Bundle App Tile CTA Click
        if (linkedAddIns && linkedAddIns.length > 0) {
            details.isSaaSBundle = true;
        }
        generateTileClickPayloadAndLogTelemetry(Constants.Telemetry.ActionModifier.AppTileCTAButton, details);
        this.context.ctaCallback({
            entity: this.props,
            entityType: Constants.EntityType.App,
            ctaType,
            actionModifier: Constants.Telemetry.ActionModifier.AppTileCTAButton,
            hasLicense: doesTenantHasLicenseForApp,
            ribbonKey
        });
    }
    renderStartingPrice() {
        const { startingPrice, billingCountryCode, offerType, entityId, locale } = this.props;
        const isOneTimePayment = isOneTimePaymentOffer(entityId);
        if ((startingPrice === null || startingPrice === void 0 ? void 0 : startingPrice.pricingData) && startingPrice.pricingData !== PricingStates.NoPricingData) {
            if (startingPrice.pricingData === PricingStates.FreeApp) {
                return `${this.context.loc('Pricing_Free', 'Free')}`;
            }
            if (startingPrice.pricingData === PricingStates.StartFromFree) {
                return `${this.context.loc('Pricing_StartsAt', 'Starts at')} ${this.context.loc('Pricing_Free', 'Free')}`;
            }
            if (startingPrice.pricingData === PricingStates.AdditionalPurchasesRequired) {
                return `${this.context.loc('AdditionalPurchaseMayBeRequired', 'Additional purchase may be required')}`;
            }
            if (startingPrice.pricingData === PricingStates.BYOL) {
                return `${this.context.loc('Bring_Your_Own_License_Shortcut', 'Bring your own license')}`;
            }
            if (typeof startingPrice.pricingData === 'object') {
                const price = getStartingFromPriceText({
                    startingPrice,
                    offerType,
                    context: this.context,
                    countryCode: billingCountryCode,
                    locale,
                    isOneTimePaymentOffer: isOneTimePayment
                });
                return `${this.context.loc('Pricing_StartsAt', 'Starts at')} ${price}`;
            }
            if (!isAvailableInThisRegion(this.props)) {
                return `${this.context.loc('Not_Available_In_Billing_Region', 'Not available in your region')}`;
            }
        }
    }
    renderPopularity() {
        if (this.props.showPopularity && this.props.popularity) {
            return /*#__PURE__*/ _jsxs("div", {
                className: "pricingText",
                children: [
                    "P#: ",
                    this.props.popularity * 1000 || 'none'
                ]
            });
        }
        return null;
    }
    getTag(keys) {
        const res = [];
        for(let i = 0; i < keys.length; i++){
            const tag = AppTag[`${keys[`${i}`]}`];
            if (tag) {
                res.push(tag);
            }
        }
        return res;
    }
    // Returns array of products an app tile is built for
    getBuiltFor() {
        const builtForList = [];
        const productTitle = getPrimaryProductUrl(this.props.primaryProduct);
        // Temporary fix for PowerApps to have their builtfor displayed as Apps
        // until we come up with the complete design
        if (this.props.products[DataMap.products.PowerApps.FilterGroup] && (this.props.products[DataMap.products.PowerApps.FilterGroup] & DataMap.products.PowerApps.FilterID) > 0) {
            builtForList.push('Apps');
        } else if (this.props.primaryProduct && productTitle && isOfficeNonSaasApp(productTitle, this.props.products)) {
            for(const key in DataMap.products){
                if (isOfficeNonSaasApp(DataMap.products[`${key}`].UrlKey) && bitMasksMatchFilter(this.props.products, DataMap.products[`${key}`])) {
                    if (!DataMap.products[`${key}`].ShortcutFilters) {
                        builtForList.push(DataMap.products[`${key}`].Title);
                    }
                }
            }
        } else {
            const parentProductTitle = getParentProductTitle(productTitle, 'dynamics-365');
            builtForList.push(parentProductTitle || this.props.builtFor);
        }
        return builtForList;
    }
    renderLinkedAddIns() {
        const { linkedAddIns, shouldShowLinkedAddIn, item } = this.props;
        const stackTokens = {
            childrenGap: 8
        };
        const textStyles = {
            root: {
                color: SharedColors.cyanBlue10,
                textDecoration: 'underline',
                whiteSpace: 'nowrap'
            }
        };
        if ((linkedAddIns === null || linkedAddIns === void 0 ? void 0 : linkedAddIns.length) && shouldShowLinkedAddIn) {
            return /*#__PURE__*/ _jsx(Stack, {
                horizontal: true,
                tokens: stackTokens,
                children: /*#__PURE__*/ _jsx(ItemsTooltip, {
                    linkedItemsIds: linkedAddIns.map(({ entityId })=>entityId),
                    title: this.context.loc('AppTileText_RelatedAddInsOnHoverText', 'This app includes the full version of these add-ins:'),
                    parentItem: item,
                    children: /*#__PURE__*/ _jsx(Text, {
                        variant: "smallPlus",
                        styles: textStyles,
                        tabIndex: 0,
                        children: this.context.loc('AppTileText_RelatedAddIns', '+included add-ins')
                    })
                })
            });
        }
    }
    renderLinkedWebApps() {
        const { linkedSaaS, shouldShowLinkedWebApps, item } = this.props;
        const stackTokens = {
            childrenGap: 4
        };
        const textStyles = {
            root: {
                color: SharedColors.cyanBlue10,
                textDecoration: 'underline',
                whiteSpace: 'nowrap'
            }
        };
        if ((linkedSaaS === null || linkedSaaS === void 0 ? void 0 : linkedSaaS.length) && shouldShowLinkedWebApps) {
            return /*#__PURE__*/ _jsx(Stack, {
                horizontal: true,
                reversed: true,
                horizontalAlign: "end",
                tokens: stackTokens,
                children: /*#__PURE__*/ _jsx(ItemsTooltip, {
                    linkedItemsIds: linkedSaaS.map(({ entityId })=>entityId),
                    title: this.context.loc('AppTileText_RelatedWebAppsOnHoverText', 'Available SaaS package (includes a full version of this add-in):'),
                    parentItem: item,
                    children: /*#__PURE__*/ _jsx(Text, {
                        variant: "smallPlus",
                        styles: textStyles,
                        tabIndex: 0,
                        children: this.context.loc('AppTileText_RelatedWebApps', 'view package options')
                    })
                })
            });
        }
    }
    // Returns brand icon for a product
    getBrandIconFromProduct(title) {
        return title === 'Web app' ? /*#__PURE__*/ _jsx("div", {
            className: 'icon-webapp-12'
        }) : /*#__PURE__*/ _jsx("div", {
            className: 'ms-BrandIcon-svg--icon16 product-icon-12 ms-BrandIcon--' + title.toLowerCase()
        });
    }
    // Returns component in the form icon + product name
    getBuiltForRenderItem(title) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "built-for-product-container",
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: "built-for-product-icon-wrapper",
                    children: /*#__PURE__*/ _jsx("div", {
                        className: "built-for-icon",
                        children: this.getBrandIconFromProduct(title)
                    })
                }),
                /*#__PURE__*/ _jsx("span", {
                    className: "built-for-product-title",
                    children: title
                })
            ]
        });
    }
    // Renders built for prduct type information for app (product type can be one or more)
    renderBuiltForProductType() {
        const builtForProducts = this.getBuiltFor();
        let builtForItems;
        const productTitle = getPrimaryProductUrl(this.props.primaryProduct);
        if ((isAppSource(this.props.embedHost) || isEmbed(this.props.embedHost)) && (isOfficeApp(productTitle) || isSaaS(productTitle))) {
            if (builtForProducts.length === 1 && (builtForProducts[0] === DataMap.products.Office365.Title || builtForProducts[0] === DataMap.products.AzureforWebApps.Title)) {
                builtForItems = /*#__PURE__*/ _jsx("div", {
                    className: "built-for-outer-wrapper",
                    children: /*#__PURE__*/ _jsx("div", {
                        className: "built-for-container",
                        children: this.getBuiltForRenderItem('Web app')
                    })
                });
            } else {
                const builtForItemsFirstRow = [];
                const builtForItemsSecondRow = [];
                for(let i = 0; i < builtForProducts.length; i++){
                    if (i < 2) {
                        builtForItemsFirstRow.push(this.getBuiltForRenderItem(builtForProducts[`${i}`]));
                    } else if (i < 4) {
                        builtForItemsSecondRow.push(this.getBuiltForRenderItem(builtForProducts[`${i}`]));
                    } else if (i >= 4) {
                        const moreHintStr = '...';
                        builtForItemsSecondRow.push(/*#__PURE__*/ _jsx("div", {
                            className: "built-for-product-container",
                            children: /*#__PURE__*/ _jsx("span", {
                                className: "product-tip",
                                children: moreHintStr
                            })
                        }));
                        break;
                    }
                }
                builtForItems = /*#__PURE__*/ _jsxs("div", {
                    className: "built-for-outer-wrapper",
                    children: [
                        builtForItemsFirstRow.length > 0 && /*#__PURE__*/ _jsx("div", {
                            className: "built-for-container",
                            children: builtForItemsFirstRow
                        }),
                        builtForItemsSecondRow.length > 0 && /*#__PURE__*/ _jsx("div", {
                            className: "built-for-container",
                            children: builtForItemsSecondRow
                        })
                    ]
                });
            }
        } else {
            builtForItems = null;
        }
        return builtForItems;
    }
    renderDescription(id, shortDescription) {
        let maxNumberOfLines = 3;
        if (this.props.AverageRating) maxNumberOfLines--;
        const builtFor = this.getBuiltFor();
        if (builtFor && builtFor.length > 2) maxNumberOfLines--;
        const style = {
            '-webkit-line-clamp': maxNumberOfLines.toString(),
            maxHeight: `${maxNumberOfLines * 17}px`
        };
        return(// eslint-disable-next-line react/forbid-dom-props
        /*#__PURE__*/ _jsx("h5", {
            style: style,
            id: 'desc-' + id,
            className: "c-paragraph-4 description",
            children: shortDescription
        }));
    }
    renderRatings() {
        const { ratingSummaries } = this.props;
        const allRatingSummaryObj = getRatingSummaryByType(ratingSummaries, Constants.Reviews.ReviewsSource.All);
        const avgRating = (allRatingSummaryObj === null || allRatingSummaryObj === void 0 ? void 0 : allRatingSummaryObj.AverageRating) || this.props.AverageRating;
        const ratingsCount = (allRatingSummaryObj === null || allRatingSummaryObj === void 0 ? void 0 : allRatingSummaryObj.TotalRatings) || this.props.NumberOfRatings;
        return ratingsCount >= 1 && {
            avgRating,
            ratingsCount,
            key: 'internal',
            text: this.context.loc('Tile_ASRatings', 'ratings')
        };
    }
    getCTAData() {
        const { isEmbedded, ctaTypes, billingCountryCode } = this.props;
        const ctaBaseData = this.getCtaBaseData();
        const { area, view } = this.getCtaExtraData();
        return (ctaBaseData === null || ctaBaseData === void 0 ? void 0 : ctaBaseData.text) && {
            id: ctaBaseData.id || Constants.JsllCTAId[Constants.CTAType[ctaBaseData.type]],
            title: ctaBaseData.text,
            area,
            view,
            onClick: this.getApp.bind(this),
            entityId: this.props.entityId,
            CtaType: this.props.offerType === OfferType.SaaSApp ? isTransactApp({
                isEmbedded,
                ctaTypes,
                appData: this.props,
                billingRegion: billingCountryCode
            }) ? Constants.JsllSaas.SaasTransactable : Constants.JsllSaas.SaasNonTransactable : Constants.JsllCTAId[Constants.CTAType[ctaBaseData.type]],
            disabled: !isAvailableInThisRegion(this.props)
        };
    }
    getCtaBaseData() {
        const { licenseManagement, startingPrice, trials, uiRole, doesTenantHasLicenseForApp } = this.props;
        if (this.isPBIVApp) {
            return this.getCtaInstall();
        } else if (isBagTransactable(this.props)) {
            return getCtaBagTransactable(uiRole, doesTenantHasLicenseForApp, licenseManagement === null || licenseManagement === void 0 ? void 0 : licenseManagement.isFreemium) === CTAInstall ? this.getCtaInstall() : this.getCtaBuyLicense();
        } else if (licenseManagement === null || licenseManagement === void 0 ? void 0 : licenseManagement.isMicrosoftManaged) {
            return this.getCtaMicrosoftManaged();
        } else if ((startingPrice === null || startingPrice === void 0 ? void 0 : startingPrice.hasFreeTrial) || trials === DataMap.trials['free-trial'].FilterID) {
            return this.getCtaTry();
        }
        return this.getCtaByActionString();
    }
    getCtaMicrosoftManaged() {
        const { uiRole, licenseManagement, doesTenantHasLicenseForApp: doesTenantHaveAppLicense, signedIn } = this.props;
        if (!isAvailableInThisRegion(this.props)) {
            return null; // This means the button would dissappear
        }
        if (!signedIn) {
            return this.getCtaRequestTrial();
        }
        const adminCanInstall = (licenseManagement === null || licenseManagement === void 0 ? void 0 : licenseManagement.isFreemium) || doesTenantHaveAppLicense;
        if (uiRole === Constants.UiRole.Admin && adminCanInstall) {
            return this.getCtaGet();
        } else {
            return this.getCtaRequestTrial();
        }
    }
    getCtaGet() {
        return {
            id: Constants.JsllCTAId[`${ActionGet}`],
            type: CTAGet,
            text: this.context.loc(ActionGet, 'Get')
        };
    }
    getCtaRequestTrial() {
        return {
            id: Constants.JsllCTAId[`${ActionRequestTrial}`],
            type: CTARequestTrial,
            text: this.context.loc(ActionRequestTrial, 'Contact Me')
        };
    }
    getCtaTry() {
        return {
            id: JsllCTAIdTry,
            type: CTATry,
            text: this.context.loc(ActionTry, 'Free Trial')
        };
    }
    getCtaInstall() {
        return {
            id: JsllCTAIdInstall,
            type: CTAInstall,
            text: this.context.loc(ActionInstall, 'Install')
        };
    }
    getCtaBuyLicense() {
        return {
            id: JsllCTAIdBuyLicense,
            type: CTABuyLicense,
            text: this.context.loc(ActionBuyLicense, 'Buy License')
        };
    }
    getCtaByActionString() {
        return {
            id: Constants.JsllCTAId[this.props.actionString] || this.props.actionString,
            text: this.context.loc(this.props.actionString)
        };
    }
    getCtaExtraData() {
        let area;
        let view;
        const linkedAddIns = this.props.linkedAddIns;
        if (linkedAddIns && linkedAddIns.length > 0) {
            if (this.props.currentView === Constants.currentView.AppDetails) {
                area = Constants.Telemetry.AreaName.ParentSaaSTile;
            } else if (this.props.currentView === Constants.currentView.AppGallery || this.props.currentView === Constants.currentView.SearchAppGallery) {
                area = Constants.Telemetry.AreaName.AppTile;
            }
            view = Constants.Telemetry.TemplateName.SaaSBundle;
        } else if (this.props.linkedSaaS) {
            view = Constants.Telemetry.AreaName.BundleAddIn;
            area = Constants.Telemetry.AreaName.AppTile;
        } else {
            area = Constants.Telemetry.AreaName.AppTile;
        }
        return {
            area,
            view
        };
    }
    getAppDetailsUrl(productType, appType) {
        return this.context.buildHref(getPDPRoute(appType), {
            productId: productType,
            entityId: this.props.entityId
        }, {
            tab: 'Overview'
        });
    }
    getProducts() {
        return this.props.products && Object.keys(this.props.products).length > 0 && getProductsList(this.props.products);
    }
    renderBadge() {
        const { entityId } = this.props;
        const badges = getBadgeTypes(this.props);
        const hasFreeTrial = badges.includes(Constants.BadgeType.FreeTrial);
        const filteredBadges = hasFreeTrial && badges.length > 3 ? badges.slice(0, 2).concat([
            Constants.BadgeType.FreeTrial
        ]) : badges.slice(0, 4);
        return this.props.isHydrated && filteredBadges.length > 0 ? /*#__PURE__*/ _jsx(Stack, {
            className: "tileBadgeContainer",
            horizontal: true,
            children: badgesInformation.filter(({ badge })=>filteredBadges.includes(badge)).map(({ src, loc = '', title = '', customClassName = '', hidden }, idx)=>/*#__PURE__*/ _jsx(Stack.Item, {
                    children: /*#__PURE__*/ _jsx(Image, {
                        className: `tileBadge ${customClassName}`,
                        hidden: hidden === null || hidden === void 0 ? void 0 : hidden(),
                        src: src,
                        title: this.context.loc(loc, title),
                        alt: this.context.loc(loc, title)
                    })
                }, `${entityId}-badge-${idx}`))
        }) : null;
    }
    renderImpl() {
        const productString = getPrimaryProductUrl(this.props.primaryProduct);
        const productData = getProductByUrlKey({
            urlKey: productString
        });
        const product = productData ? productData.UrlKey : productString;
        const isPrivate = isPrivateOffer(this.props.appType);
        const appDetailUrl = this.getAppDetailsUrl(product, this.props.appType);
        const { showPopularity, popularity, shouldShowLinkedAddIn, shouldShowLinkedWebApps, ribbonKey = '' } = this.props;
        return /*#__PURE__*/ _jsx(BaseTile, {
            totalTiles: this.props.totalTiles,
            badge: this.renderBadge(),
            id: this.props.entityId,
            componentInstance: this.componentInstance,
            item: this.props.item,
            iconURL: this.props.iconURL,
            title: this.props.title,
            tileDataRequestId: this.props.tileDataRequestId,
            publisher: this.props.publisher,
            description: this.props.shortDescription,
            ctaData: this.getCTAData(),
            detailUrl: appDetailUrl,
            tileIndex: this.props.tileIndex,
            currentView: this.props.currentView,
            userFavouriteEntityType: isPrivate ? null : Constants.UserFavourite.ApplicationTypes.ITApplications,
            linkedAddIns: this.renderLinkedAddIns(),
            linkedWebApps: this.renderLinkedWebApps(),
            price: this.renderStartingPrice(),
            rating: this.renderRatings(),
            openTileCallback: this.context.openTileCallback,
            products: this.getProducts(),
            context: this.context,
            popularity: showPopularity && popularity,
            shouldShowLinkedAddIn: shouldShowLinkedAddIn,
            shouldShowLinkedWebApps: shouldShowLinkedWebApps,
            badgeTypes: getBadgeTypes(this.props),
            ribbonKey: ribbonKey
        });
    }
    constructor(props, context){
        super(props, context), _define_property(this, "context", void 0), _define_property(this, "componentInstance", void 0), _define_property(this, "isPBIVApp", isPowerBIVisuals(getPrimaryProductUrl(this.props.primaryProduct)));
        this.componentInstance = v4();
    }
}
AppTile.contextTypes = {
    loc: PropTypes.func,
    locParams: PropTypes.func,
    buildHref: PropTypes.func,
    ctaCallback: PropTypes.func,
    renderErrorModal: PropTypes.func,
    openTileCallback: PropTypes.func
};
